/**
 * Livewire Click Loader
 * Shows a loader indicator on the clicked element during Livewire requests
 */

document.addEventListener('DOMContentLoaded', () => {
    setupLivewireLoader();
});

document.addEventListener('livewire:init', () => {
    setupLivewireLoader();
});

// Fallback setup if Livewire is already loaded
if (window.Livewire) {
    setupLivewireLoader();
}

function setupLivewireLoader() {
    // Listen for click events that might trigger Livewire actions
    document.addEventListener('click', (event) => {
        const target = event.target;
        
        // Find element with Livewire click handler
        const livewireElement = findLivewireElement(target);
        if (livewireElement) {
            // Delay slightly to ensure the click was registered
            requestAnimationFrame(() => {
                showLoader(livewireElement);
            });
        }
    });

    // Handle wire:model inputs (typing, changing values)
    document.addEventListener('input', (event) => {
        const target = event.target;
        
        if (target.hasAttribute('wire:model')) {
            requestAnimationFrame(() => {
                showLoader(target);
            });
        }
    });

    document.addEventListener('change', (event) => {
        const target = event.target;
        
        if (target.hasAttribute('wire:model') || target.hasAttribute('wire:change')) {
            requestAnimationFrame(() => {
                showLoader(target);
            });
        }
    });

    // Handle form submissions
    document.addEventListener('submit', (event) => {
        const form = event.target;
        
        if (form.hasAttribute('wire:submit')) {
            const submitButton = form.querySelector('[type="submit"], button');
            if (submitButton) {
                requestAnimationFrame(() => {
                    showLoader(submitButton);
                });
            }
        }
    });
}

function findLivewireElement(element) {
    if (!element) return null;
    
    // Check the element itself for Livewire attributes
    if (hasLivewireAction(element)) {
        return element;
    }

    // Check parent elements
    let parent = element.parentElement;
    while (parent) {
        if (hasLivewireAction(parent)) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return null;
}

function hasLivewireAction(element) {
    if (!element) return false;
    
    const livewireAttributes = [
        'wire:click',
        'wire:change',
        'wire:submit',
        'wire:model',
        'wire:keydown',
        'wire:focus',
        'wire:blur',
        'wire:navigate'
    ];

    for (const attr of livewireAttributes) {
        if (element.hasAttribute(attr)) {
            return true;
        }
    }

    return false;
}

function showLoader(element) {
    if (!element) return;

    // Don't show loader if already showing
    if (element.classList.contains('lw-loading')) return;

    // Store original styles
    element.dataset.originalPointerEvents = element.style.pointerEvents || '';
    element.dataset.originalDisabled = element.disabled ? 'true' : 'false';

    // Create loader overlay element
    const loader = document.createElement('span');
    loader.className = 'lw-loader';
    loader.innerHTML = getLoaderHTML();
    
    // Style the loader overlay
    loader.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.5);
        border-radius: inherit;
        z-index: 9999;
    `;

    // Make element positioned if not already (check for inline style or computed style)
    const inlinePosition = element.style.position;
    const computedPosition = window.getComputedStyle(element).position;
    element.style.position = (inlinePosition === 'static' || !inlinePosition) ? 'relative' : inlinePosition;

    // Make overflow hidden to contain the loader
    element.style.overflow = 'hidden';

    // Add loader to element
    element.appendChild(loader);

    // Mark element as loading
    element.classList.add('lw-loading');

    // If element is a button or link, disable it
    if (element.tagName === 'BUTTON' || element.tagName === 'A') {
        element.disabled = true;
        element.style.pointerEvents = 'none';
    }
}

function cleanUpAllLoaders() {
    // Remove all loader elements
    document.querySelectorAll('.lw-loader').forEach(el => el.remove());
    
    // Reset all loading elements
    document.querySelectorAll('.lw-loading').forEach(el => {
        el.classList.remove('lw-loading');
        el.style.overflow = '';
        el.style.pointerEvents = el.dataset.originalPointerEvents || '';
        
        if (el.tagName === 'BUTTON' || el.tagName === 'A') {
            el.disabled = el.dataset.originalDisabled === 'true';
        }
    });
}

function getLoaderHTML() {
    return `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
        </svg>
        <style>
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        </style>
    `;
}

// Auto-cleanup every 10 seconds to handle any stuck loaders
setInterval(() => {
    cleanUpAllLoaders();
}, 10000);